"use client";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import CTA from "@/components/ui/CTA";
import Link from "next/link";
import Image from "next/image";
import { WORK_ITEMS, type WorkItem } from "@/lib/content/work";
import { resolveWorkSpotlightCopy } from "@/lib/work/work-api";
import { useMotion } from "@/contexts/MotionContext";
import { useRef, useEffect, ReactNode } from "react";
import { LazyAsciiVideoCanvasBackgroundContained } from "@/components/animations/LazyAsciiVideoCanvasBackground";
import SafeImage from "@/components/ui/SafeImage";
import { MEDIA } from "@/lib/assets";
import { SERVICE_BUCKETS } from "@/lib/config/services.config";
import { SERVICE_LANDING_DEFAULT_CAPABILITIES } from "@/lib/content/service-configs";
import { CARD_TEXTURE_SURFACE } from "@/lib/styles/surfaces";

export interface ICPContext {
  roles: string;
  context: string;
  needs: string;
  coreFear: string;
}

export interface ServiceCapability {
  title: string;
  description: string;
  descriptionLink?: {
    href: string;
    label: string;
  };
}

const HOVER_COLORS: Record<string, { border: string; text: string }> = {
  "brand-teal": {
    border: "hover:border-brand-teal",
    text: "group-hover:text-brand-teal",
  },
  "brand-pink": {
    border: "hover:border-brand-pink",
    text: "group-hover:text-brand-pink",
  },
  "brand-yellow": {
    border: "hover:border-brand-yellow",
    text: "group-hover:text-brand-yellow",
  },
  "brand-peach": {
    border: "hover:border-brand-peach",
    text: "group-hover:text-brand-peach",
  },
};

function WorkSpotlightCopyBlock({
  item,
  className,
}: {
  item: WorkItem;
  className: string;
}) {
  const spotlight = resolveWorkSpotlightCopy(item);
  const fallback =
    typeof item.description === "string" ? item.description : "";

  if (!spotlight && !fallback) {
    return null;
  }

  return (
    <div className={className}>
      {Array.isArray(spotlight)
        ? spotlight.map((para, idx) => (
            <p key={idx} className={idx > 0 ? "mt-2" : ""}>
              {para}
            </p>
          ))
        : (
            <p>{spotlight || fallback}</p>
          )}
    </div>
  );
}

const SERVICE_LINK_SHAPES: Record<string, string> = Object.fromEntries(
  SERVICE_BUCKETS.map((bucket) => [bucket.id, bucket.icon]),
);

export interface ServiceConfig {
  id: string;
  title: string;
  heroTitle: string;
  eyebrow?: string;
  tagline: string;
  description: string;
  mainCopyHeading?: string;
  mainCopy: (string | ReactNode)[];
  capabilities?: ServiceCapability[];
  icpContext: ICPContext;
  workCategory?: string;
  workPageHref?: string;
  workPageTitle?: string;
  workItemIds?: string[];
  ndaMessage?: {
    title: string;
    description: string;
    ctaHref: string;
    ctaText: string;
  };
  otherServices: Array<{
    title: string;
    tagline: string;
    href: string;
    hoverColor: string;
  }>;
}

interface ServiceLandingPageProps {
  config: ServiceConfig;
}

export default function ServiceLandingPage({
  config,
}: ServiceLandingPageProps) {
  const { isMotionEnabled } = useMotion();
  const capabilityBlocks =
    config.capabilities ?? SERVICE_LANDING_DEFAULT_CAPABILITIES;

  // Filter work items - use specific IDs if provided, otherwise filter by category
  const workItems = config.workItemIds
    ? WORK_ITEMS.filter((item) => config.workItemIds!.includes(item.id)).sort(
        (a, b) => {
          const indexA = config.workItemIds!.indexOf(a.id);
          const indexB = config.workItemIds!.indexOf(b.id);
          return indexA - indexB;
        },
      )
    : config.workCategory
      ? WORK_ITEMS.filter((item) => {
          if (!item.listed) return false;
          // Exclude COVID School Hub from growth page
          if (config.id === "growth" && item.id === "covid-school-hub")
            return false;
          return item.categories?.includes(config.workCategory!);
        }).slice(0, 3) // Show first 3 examples
      : [];

  const firstItem = workItems[0];
  const otherItems = workItems.slice(1);
  /** Full-bleed brand tint behind work media reads harsh on medical; keep media clean. */
  const showWorkMediaColorTint = config.id !== "medical";

  return (
    <div className="relative isolate min-h-screen bg-tan dark:bg-background-dark">
      <div className="relative z-10">
        <Nav />
        <main>
          {/* Hero */}
          <section className="relative overflow-hidden pt-28 pb-14 md:pt-28 md:pb-16">
            <LazyAsciiVideoCanvasBackgroundContained
              mode="contained"
              videoSrc={MEDIA.heroCloudsVideo}
              posterSrc={MEDIA.heroCloudsPoster}
            />
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-28 bg-gradient-to-b from-transparent via-tan/70 to-tan dark:via-[#2B2828]/75 dark:to-[#2B2828]"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[3] h-10 shadow-[0_-14px_28px_rgba(0,0,0,0.08)] dark:shadow-[0_-14px_28px_rgba(0,0,0,0.35)]"
              aria-hidden
            />
            <div className="relative z-[4] container mx-auto px-4">
              <div className="max-w-7xl">
                {config.eyebrow && (
                  <p className="text-xs md:text-sm font-semibold text-text-light/80 dark:text-text-dark/85 mb-6 uppercase tracking-[0.16em] max-w-2xl leading-relaxed">
                    {config.eyebrow}
                  </p>
                )}
                <h1 className="text-6xl md:text-7xl lg:text-8xl font-medium leading-[0.95] text-text-light dark:text-text-dark mb-10 md:mb-12">
                  {config.heroTitle}
                </h1>
                <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                  <p className="text-2xl text-text-light dark:text-text-dark font-light leading-relaxed max-w-3xl [text-shadow:0_1px_0_rgba(255,255,255,0.82),0_2px_10px_rgba(0,0,0,0.2),0_4px_18px_rgba(0,0,0,0.12)] dark:[text-shadow:0_1px_1px_rgba(0,0,0,0.62),0_3px_10px_rgba(0,0,0,0.72),0_6px_16px_rgba(0,0,0,0.5)]">
                    {config.tagline}
                  </p>
                  {config.workPageHref && (
                    <Link
                      href={config.workPageHref}
                      className="group inline-flex shrink-0 self-start lg:self-end items-center gap-2 rounded-lg bg-[rgba(254,249,236,0.72)] px-3 py-1.5 whitespace-nowrap text-xl font-light text-text-light transition-colors hover:text-text-light/80 dark:bg-[rgba(43,40,40,0.66)] dark:text-text-dark dark:hover:text-text-dark/85"
                    >
                      <Image
                        src={
                          SERVICE_LINK_SHAPES[config.id] ??
                          SERVICE_LINK_SHAPES.medical
                        }
                        alt=""
                        width={18}
                        height={18}
                        className="h-[18px] w-[18px] object-contain transition-transform group-hover:translate-x-0.5"
                        aria-hidden
                      />
                      {config.workPageTitle || "See All Work"} →
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Examples - Large, prominent display */}
          {(config.workCategory ||
            (config.workItemIds && config.workItemIds.length > 0)) && (
            <section className="pb-24">
              <div className="container mx-auto px-4">
                <div className="max-w-7xl">
                  {workItems.length > 0 ? (
                    <>
                      {/* Layout: Large image on left, two stacked on right */}
                      <div className="grid md:grid-cols-[2fr_1fr] gap-12 mb-12">
                        {/* First item - Large on left */}
                        {firstItem && (
                          <div
                            className={`rounded-2xl p-6 ${CARD_TEXTURE_SURFACE}`}
                          >
                            {firstItem.slug ? (
                              <Link
                                href={`/work/${firstItem.slug}`}
                                className="block group"
                              >
                                <div className="relative aspect-[16/9] rounded-xl overflow-hidden mb-6">
                                  {showWorkMediaColorTint ? (
                                    <div
                                      className="absolute inset-0 -m-4 rounded-xl opacity-20 transition-opacity group-hover:opacity-30"
                                      style={{
                                        backgroundColor: firstItem.color,
                                      }}
                                    />
                                  ) : null}
                                  <div className="relative w-full h-full">
                                    {firstItem.featuredMedia.type ===
                                    "video" ? (
                                      <WorkVideo
                                        src={firstItem.featuredMedia.url}
                                        isMotionEnabled={isMotionEnabled}
                                      />
                                    ) : (
                                      <SafeImage
                                        src={firstItem.featuredMedia.url}
                                        alt={firstItem.title}
                                        fill
                                        className="object-cover"
                                      />
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-start gap-4">
                                  <div className="w-16 h-16 rounded-full overflow-hidden bg-black/5 dark:bg-white/10 flex-shrink-0">
                                    <SafeImage
                                      src={firstItem.client.logo}
                                      alt={firstItem.client.name}
                                      width={64}
                                      height={64}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <h3 className="text-3xl font-medium text-text-light dark:text-text-dark mb-1 group-hover:opacity-70 transition-opacity">
                                      {firstItem.title}
                                    </h3>
                                    <p className="text-base text-text-light/80 dark:text-text-dark/85 leading-tight">
                                      {firstItem.client.name}
                                    </p>
                                  </div>
                                </div>
                                <WorkSpotlightCopyBlock
                                  item={firstItem}
                                  className="mt-4 text-lg md:text-xl text-text-light dark:text-text-dark font-light leading-relaxed"
                                />
                              </Link>
                            ) : (
                              <div>
                                <div className="relative aspect-[16/9] rounded-xl overflow-hidden mb-6">
                                  {showWorkMediaColorTint ? (
                                    <div
                                      className="absolute inset-0 -m-4 rounded-xl opacity-20"
                                      style={{
                                        backgroundColor: firstItem.color,
                                      }}
                                    />
                                  ) : null}
                                  <div className="relative w-full h-full">
                                    {firstItem.featuredMedia.type ===
                                    "video" ? (
                                      <WorkVideo
                                        src={firstItem.featuredMedia.url}
                                        isMotionEnabled={isMotionEnabled}
                                      />
                                    ) : (
                                      <SafeImage
                                        src={firstItem.featuredMedia.url}
                                        alt={firstItem.title}
                                        fill
                                        className="object-cover"
                                      />
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-start gap-4">
                                  <div className="w-16 h-16 rounded-full overflow-hidden bg-black/5 dark:bg-white/10 flex-shrink-0">
                                    <SafeImage
                                      src={firstItem.client.logo}
                                      alt={firstItem.client.name}
                                      width={64}
                                      height={64}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <h3 className="text-3xl font-medium text-text-light dark:text-text-dark mb-2">
                                      {firstItem.title}
                                    </h3>
                                    <p className="text-base text-text-light/80 dark:text-text-dark/85">
                                      {firstItem.client.name}
                                    </p>
                                  </div>
                                </div>
                                <WorkSpotlightCopyBlock
                                  item={firstItem}
                                  className="mt-4 text-lg md:text-xl text-text-light dark:text-text-dark font-light leading-relaxed"
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Other items - Stacked on right */}
                        {otherItems.length > 0 && (
                          <div className="flex flex-col gap-12">
                            {otherItems.map((item) => (
                              <div
                                key={item.id}
                                className={`rounded-xl p-6 ${CARD_TEXTURE_SURFACE}`}
                              >
                                {item.slug ? (
                                  <Link
                                    href={`/work/${item.slug}`}
                                    className="block group"
                                  >
                                    <div className="relative aspect-video rounded-lg overflow-hidden mb-4">
                                      {showWorkMediaColorTint ? (
                                        <div
                                          className="absolute inset-0 -m-2 rounded-lg opacity-20 transition-opacity group-hover:opacity-30"
                                          style={{
                                            backgroundColor: item.color,
                                          }}
                                        />
                                      ) : null}
                                      <div className="relative w-full h-full">
                                        {item.featuredMedia.type === "video" ? (
                                          <WorkVideo
                                            src={item.featuredMedia.url}
                                            isMotionEnabled={isMotionEnabled}
                                          />
                                        ) : (
                                          <SafeImage
                                            src={item.featuredMedia.url}
                                            alt={item.title}
                                            fill
                                            className="object-cover"
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-full overflow-hidden bg-black/5 dark:bg-white/10 flex-shrink-0">
                                          <SafeImage
                                            src={item.client.logo}
                                            alt={item.client.name}
                                            width={48}
                                            height={48}
                                            className="w-full h-full object-cover"
                                          />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <h3 className="text-2xl font-medium text-text-light dark:text-text-dark mb-0 group-hover:opacity-70 transition-opacity leading-tight">
                                            {item.title}
                                          </h3>
                                          <p className="text-sm text-text-light/80 dark:text-text-dark/85 mb-1 leading-tight">
                                            {item.client.name}
                                          </p>
                                        </div>
                                      </div>
                                      {(resolveWorkSpotlightCopy(item) ||
                                        (typeof item.description === "string" &&
                                          item.description)) && (
                                        <p className="text-sm text-text-light dark:text-text-dark font-light leading-relaxed mt-3">
                                          {resolveWorkSpotlightCopy(item) ||
                                            (typeof item.description ===
                                            "string"
                                              ? item.description
                                              : "")}
                                        </p>
                                      )}
                                    </div>
                                  </Link>
                                ) : (
                                  <div>
                                    <div className="relative aspect-video rounded-lg overflow-hidden mb-4">
                                      {showWorkMediaColorTint ? (
                                        <div
                                          className="absolute inset-0 -m-2 rounded-lg opacity-20"
                                          style={{
                                            backgroundColor: item.color,
                                          }}
                                        />
                                      ) : null}
                                      <div className="relative w-full h-full">
                                        {item.featuredMedia.type === "video" ? (
                                          <WorkVideo
                                            src={item.featuredMedia.url}
                                            isMotionEnabled={isMotionEnabled}
                                          />
                                        ) : (
                                          <SafeImage
                                            src={item.featuredMedia.url}
                                            alt={item.title}
                                            fill
                                            className="object-cover"
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                      <div className="w-12 h-12 rounded-full overflow-hidden bg-black/5 dark:bg-white/10 flex-shrink-0">
                                        <SafeImage
                                          src={item.client.logo}
                                          alt={item.client.name}
                                          width={48}
                                          height={48}
                                          className="w-full h-full object-cover"
                                        />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <h3 className="text-2xl font-medium text-text-light dark:text-text-dark mb-2">
                                          {item.title}
                                        </h3>
                                        <p className="text-sm text-text-light/80 dark:text-text-dark/85">
                                          {item.client.name}
                                        </p>
                                      </div>
                                    </div>
                                    {(resolveWorkSpotlightCopy(item) ||
                                      (typeof item.description === "string" &&
                                        item.description)) && (
                                      <p className="mt-3 text-sm text-text-light dark:text-text-dark font-light leading-relaxed">
                                        {resolveWorkSpotlightCopy(item) ||
                                          (typeof item.description === "string"
                                            ? item.description
                                            : "")}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  ) : null}

                  {workItems.length === 0 && config.ndaMessage && (
                    <div
                      className={`rounded-3xl bg-repeat px-10 py-20 ${CARD_TEXTURE_SURFACE}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-10">
                        <div className="flex-1">
                          <h3 className="text-4xl font-medium text-text-light dark:text-text-dark mb-6">
                            {config.ndaMessage.title}
                          </h3>
                          <p className="text-xl text-text-light dark:text-text-dark leading-relaxed">
                            {config.ndaMessage.description}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <CTA
                            href={config.ndaMessage.ctaHref}
                            className="text-lg"
                          >
                            {config.ndaMessage.ctaText}
                          </CTA>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Main Copy */}
          <section className="relative overflow-hidden py-12 md:py-16">
            <LazyAsciiVideoCanvasBackgroundContained
              mode="contained"
              videoSrc={MEDIA.heroCloudsVideo}
              posterSrc={MEDIA.heroCloudsPoster}
            />
            <div
              className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-b from-tan/58 via-tan/35 to-tan/72 dark:from-[#2B2828]/62 dark:via-[#2B2828]/42 dark:to-[#2B2828]/76"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-x-0 top-0 z-[3] h-20 bg-gradient-to-b from-black/14 to-transparent dark:from-black/34"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[3] h-20 bg-gradient-to-t from-black/14 to-transparent dark:from-black/34"
              aria-hidden
            />
            <div className="relative z-[4] container mx-auto px-4">
              <div className="max-w-7xl">
                {config.mainCopyHeading && (
                  <div className="mb-8 flex items-start justify-between gap-4">
                    <h2 className="min-w-0 max-w-full flex-1 text-4xl font-medium text-text-light dark:text-text-dark md:max-w-[min(66vw,52rem)] md:text-5xl">
                      {config.mainCopyHeading}
                    </h2>
                    {config.id === "medical" ? (
                      <Image
                        src={SERVICE_LINK_SHAPES.medical}
                        alt=""
                        width={34}
                        height={34}
                        className="mt-1 h-8 w-8 shrink-0 object-contain opacity-80 md:h-9 md:w-9"
                        aria-hidden
                      />
                    ) : null}
                  </div>
                )}
                <div className="relative rounded-2xl card-texture-surface p-8 md:p-12">
                  <div
                    className="pointer-events-none absolute inset-0 rounded-2xl bg-[rgba(254,249,236,0.84)] dark:bg-[rgba(43,40,40,0.72)]"
                    aria-hidden
                  />
                  <div className="relative z-[1] grid md:grid-cols-2 gap-8 md:gap-12">
                    {/* First paragraph */}
                    {config.mainCopy[0] && (
                      <div>
                        {typeof config.mainCopy[0] === "string" ? (
                          <p className="text-2xl md:text-3xl text-text-light dark:text-text-dark font-normal leading-relaxed">
                            {config.mainCopy[0]}
                          </p>
                        ) : (
                          <div className="text-2xl md:text-3xl text-text-light dark:text-text-dark font-normal leading-relaxed">
                            {config.mainCopy[0]}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Second paragraph */}
                    {config.mainCopy[1] && (
                      <div>
                        {typeof config.mainCopy[1] === "string" ? (
                          <p className="text-2xl md:text-3xl text-text-light dark:text-text-dark font-light leading-relaxed">
                            {config.mainCopy[1]}
                          </p>
                        ) : (
                          <div className="text-2xl md:text-3xl text-text-light dark:text-text-dark font-light leading-relaxed">
                            {config.mainCopy[1]}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Additional paragraphs */}
                    {config.mainCopy.slice(2).map((paragraph, index) => (
                      <div
                        key={index + 2}
                        className={index === 0 ? "md:col-span-2" : ""}
                      >
                        {typeof paragraph === "string" ? (
                          <p className="text-xl md:text-2xl text-text-light dark:text-text-dark font-light leading-relaxed">
                            {paragraph}
                          </p>
                        ) : (
                          <div className="text-xl md:text-2xl text-text-light dark:text-text-dark font-light leading-relaxed">
                            {paragraph}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Capabilities Section */}
          <section className="py-24">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl">
                <div className="grid items-center gap-16 md:grid-cols-2">
                  {/* Left column - static graphic */}
                  <div
                    className={`relative min-h-[420px] overflow-hidden rounded-2xl p-6 md:min-h-[500px] md:p-8 ${CARD_TEXTURE_SURFACE}`}
                  >
                    <div className="relative h-[360px] w-full overflow-hidden rounded-xl md:h-[452px]">
                      <SafeImage
                        src="/assets/graphics/prod-design-card.webp"
                        alt=""
                        fill
                        className="object-contain"
                      />
                    </div>
                  </div>

                  {/* Right column - service-specific capability copy */}
                  <div className="space-y-12">
                    {capabilityBlocks.map((block) => (
                      <div key={block.title}>
                        <h3 className="mb-4 text-2xl font-medium text-text-light dark:text-text-dark md:text-3xl">
                          {block.title}
                        </h3>
                        <p className="text-xl font-light leading-relaxed text-text-light dark:text-text-dark">
                          {block.description}
                          {block.descriptionLink ? (
                            <>
                              {" "}
                              <Link
                                href={block.descriptionLink.href}
                                className="underline decoration-current/60 underline-offset-4 hover:decoration-current"
                              >
                                {block.descriptionLink.label}
                              </Link>
                              .
                            </>
                          ) : null}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-24 pb-12">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl">
                <div
                  className={`rounded-2xl border-2 card-texture-surface p-8 md:p-12 ${
                    config.id === "medical"
                      ? "border-brand-teal/20 bg-brand-teal/50 dark:border-brand-teal/35 dark:bg-brand-teal/18"
                      : config.id === "growth"
                        ? "border-brand-pink/20 bg-brand-pink/50 dark:border-brand-pink/35 dark:bg-brand-pink/18"
                        : "border-brand-yellow/20 bg-brand-yellow/50 dark:border-brand-yellow/35 dark:bg-brand-yellow/18"
                  }`}
                >
                  <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
                    <div className="flex-1 max-w-4xl text-neutral-900 dark:text-white">
                      <h2 className="text-5xl font-medium mb-8 text-left">
                        Ready to build something that matters?
                      </h2>
                      <p className="text-xl font-light leading-relaxed mb-8 text-left">
                        We&rsquo;re especially helpful when the path forward
                        isn&rsquo;t obvious yet. Let&rsquo;s discuss your
                        challenge and map out the smartest path forward.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <CTA href="/contact">Set Up a Strategy Call</CTA>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Other Services */}
          {config.otherServices.length > 0 && (
            <section className="py-12 pb-32">
              <div className="container mx-auto px-4">
                <div className="max-w-7xl">
                  <h2 className="text-sm font-medium text-text-light/80 dark:text-text-dark/85 mb-6 uppercase tracking-wide">
                    Other Services
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {config.otherServices.map((service, index) => {
                      const colors = HOVER_COLORS[service.hoverColor] || {
                        border: "",
                        text: "",
                      };
                      return (
                        <Link
                          key={index}
                          href={service.href}
                          className={`group rounded-lg p-6 border-2 border-transparent ${colors.border} transition-all ${CARD_TEXTURE_SURFACE}`}
                        >
                          <h3 className="text-lg font-medium text-text-light dark:text-text-dark mb-2">
                            {service.title} →
                          </h3>
                          <p className="text-sm text-text-light/90 dark:text-text-dark/90 font-light leading-relaxed">
                            {service.tagline}
                          </p>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </div>
  );
}

// Video component with motion control
function WorkVideo({
  src,
  isMotionEnabled,
}: {
  src: string;
  isMotionEnabled: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      if (isMotionEnabled) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isMotionEnabled]);

  return (
    <video
      ref={videoRef}
      src={src}
      className="w-full h-full object-cover"
      muted
      loop
      playsInline
    />
  );
}
